import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const AUTH_DRIVER = import.meta.env.VITE_AUTH_DRIVER || 'dev';
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID || '';
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID || '';
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE || '';

let msal: PublicClientApplication | null = null;
let account: AccountInfo | null = null;

async function msalClient(): Promise<PublicClientApplication> {
  if (!msal) {
    msal = new PublicClientApplication({
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'sessionStorage' },
    });
    await msal.initialize();
    const redirect = await msal.handleRedirectPromise();
    account = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
  }
  return msal;
}

export async function loginMicrosoft(): Promise<void> {
  const client = await msalClient();
  const result = await client.loginPopup({ scopes: ['openid', 'profile', 'email', apiScope].filter(Boolean) });
  account = result.account;
}

export async function logoutMicrosoft(): Promise<void> {
  const client = await msalClient();
  if (account) await client.logoutPopup({ account });
  account = null;
}

async function accessToken(): Promise<string | null> {
  if (AUTH_DRIVER !== 'entra') return null;
  const client = await msalClient();
  if (!account) account = client.getAllAccounts()[0] ?? null;
  if (!account) return null;
  try {
    const result = await client.acquireTokenSilent({ account, scopes: [apiScope] });
    return result.accessToken;
  } catch {
    const result = await client.acquireTokenPopup({ account, scopes: [apiScope] });
    return result.accessToken;
  }
}

export function devUserEmail(): string {
  return localStorage.getItem('consultorio.devUserEmail') || 'admin@ticolweb.local';
}

export function setDevUserEmail(email: string): void {
  localStorage.setItem('consultorio.devUserEmail', email);
  window.dispatchEvent(new Event('consultorio-auth-change'));
}

export function authDriver(): string {
  return AUTH_DRIVER;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const isForm = options.body instanceof FormData;
  if (!isForm && options.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (AUTH_DRIVER === 'dev') headers.set('x-dev-user-email', devUserEmail());
  else {
    const token = await accessToken();
    if (!token) throw new Error('Debe iniciar sesión con Microsoft.');
    headers.set('authorization', `Bearer ${token}`);
  }
  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: response.statusText }));
    const error = new Error(body.message || 'La operación no pudo completarse.') as Error & { details?: unknown; status?: number };
    error.details = body.details;
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const jsonBody = (value: unknown): Pick<RequestInit, 'body'> => ({ body: JSON.stringify(value) });

async function authenticatedHeaders(): Promise<Headers> {
  const headers = new Headers();
  if (AUTH_DRIVER === 'dev') headers.set('x-dev-user-email', devUserEmail());
  else {
    const token = await accessToken();
    if (!token) throw new Error('Debe iniciar sesión con Microsoft.');
    headers.set('authorization', `Bearer ${token}`);
  }
  return headers;
}

export async function authenticatedBlobUrl(path: string): Promise<{ url: string; contentType: string }> {
  const response = await fetch(`${API_BASE}${path}`, { headers: await authenticatedHeaders() });
  if (!response.ok) throw new Error('No fue posible cargar el archivo.');
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), contentType: response.headers.get('content-type') ?? blob.type };
}

export async function authenticatedPostBlobUrl(
  path: string,
  body: unknown,
): Promise<{ url: string; contentType: string }> {
  const headers = await authenticatedHeaders();
  headers.set('content-type', 'application/json');
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(payload.message || 'No fue posible generar la previsualización.');
  }
  const blob = await response.blob();
  return {
    url: URL.createObjectURL(blob),
    contentType: response.headers.get('content-type') ?? blob.type,
  };
}

export async function downloadAuthenticated(path: string, fileName: string): Promise<void> {
  const headers = await authenticatedHeaders();
  const response = await fetch(`${API_BASE}${path}`, { headers });
  if (!response.ok) throw new Error('No fue posible descargar el archivo.');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
